import { supabase } from "../lib/supabase";
import { timetableService } from "./timetableService";

const missingMigration = (error) => {
  const message = error?.message || "";
  return (
    error?.code === "PGRST205" ||
    error?.code === "PGRST202" ||
    /get_summative_timetable_units/i.test(message) ||
    (/summative_assessment_timetable/i.test(message) &&
      /not found|does not exist|schema cache/i.test(message))
  );
};

const readableError = (error, fallback) => {
  if (missingMigration(error)) {
    return new Error(
      `${fallback} Apply migrations 20260101000100_summative_assessment_timetable.sql through 20260101000113_timetable_room_capacity_counts.sql in order, then refresh.`,
    );
  }
  return error instanceof Error ? error : new Error(error?.message || fallback);
};

const attachRooms = async (rows) => {
  const values = rows || [];
  const roomIds = [
    ...new Set(values.map((row) => row?.room_id).filter(Boolean).map(String)),
  ];
  if (!roomIds.length) return values;

  let result = await supabase
    .from("rooms")
    .select("id, code, name, room_type, capacity, status, location, department_id")
    .in("id", roomIds);
  if (result.error) {
    result = await supabase
      .from("rooms")
      .select("id, name, room_type, capacity, status, department_id")
      .in("id", roomIds);
  }
  if (result.error) {
    result = await supabase
      .from("rooms")
      .select("id, name, room_type, capacity, is_active, department_id")
      .in("id", roomIds);
  }
  if (result.error) return values;
  const roomMap = Object.fromEntries(
    (result.data || []).map((room) => [String(room.id), room]),
  );
  return values.map((row) => ({
    ...row,
    room: roomMap[String(row.room_id)] || null,
  }));
};

export const summativeAssessmentTimetableService = {
  async getEligibleUnits(academicYearId, semesterId) {
    if (!academicYearId || !semesterId) return [];
    const { data, error } = await supabase.rpc(
      "get_summative_timetable_units",
      {
        target_year: academicYearId,
        target_semester: semesterId,
      },
    );
    if (error)
      throw readableError(
        error,
        "Units available for assessment scheduling could not be loaded.",
      );
    const rows = (data || []).map((row) => row?.unit || row).filter(Boolean);
    const unitIds = rows.map((unit) => unit.id).filter(Boolean);
    let trainerByUnit = {};
    if (unitIds.length) {
      const [approvedResult, legacyResult] = await Promise.all([
        supabase
          .from("trainer_assignments")
          .select("unit_id, trainer_id, approval_status, created_at")
          .in("unit_id", unitIds)
          .ilike("approval_status", "approved")
          .order("created_at", { ascending: false }),
        supabase
          .from("unit_assignments")
          .select("unit_id, trainer_id, status, created_at")
          .in("unit_id", unitIds)
          // Legacy assignments were stored as `Active` while newer rows use
          // lowercase values; the database trigger normalises this too.
          .ilike("status", "active")
          .order("created_at", { ascending: false }),
      ]);
      if (approvedResult.error)
        throw readableError(
          approvedResult.error,
          "Approved Unit Trainer assignments could not be loaded.",
        );
      if (legacyResult.error)
        throw readableError(
          legacyResult.error,
          "Active Unit Trainer assignments could not be loaded.",
        );
      const assignments = [
        ...(approvedResult.data || []).map((row) => ({
          ...row,
          source: "approved",
        })),
        ...(legacyResult.data || []).map((row) => ({
          ...row,
          source: "legacy",
        })),
      ];
      const trainerIds = [
        ...new Set(assignments.map((row) => row.trainer_id).filter(Boolean)),
      ];
      const { data: trainers } = trainerIds.length
        ? await supabase
            .from("user_profiles")
            .select("id, full_name")
            .in("id", trainerIds)
        : { data: [] };
      const trainerMap = Object.fromEntries(
        (trainers || []).map((trainer) => [String(trainer.id), trainer]),
      );
      assignments.forEach((assignment) => {
        if (!trainerByUnit[assignment.unit_id])
          trainerByUnit[assignment.unit_id] = trainerMap[
            String(assignment.trainer_id)
          ] || { id: assignment.trainer_id };
      });
    }
    let capacityByUnit = {};
    if (unitIds.length) {
      const { data: capacityRows, error: capacityError } = await supabase.rpc(
        "get_timetable_unit_capacity_counts",
        { target_year: academicYearId, target_semester: semesterId },
      );
      if (!capacityError) {
        capacityByUnit = Object.fromEntries(
          (capacityRows || []).map((row) => [String(row.unit_id), row]),
        );
      }
    }
    return rows
      .filter(
        (unit) =>
          unit.is_active !== false &&
          (Number(unit.exam_theory_hours || 0) > 0 ||
            Number(unit.exam_practical_hours || 0) > 0),
      )
      .map((unit) => ({
        ...unit,
        chief_invigilator: trainerByUnit[unit.id] || null,
        timetable_enrolled_students: Number(
          capacityByUnit[String(unit.id)]?.enrolled_students || 0,
        ),
        timetable_booked_students: Number(
          capacityByUnit[String(unit.id)]?.booked_students || 0,
        ),
        timetable_combined_enrolled_students: Number(
          capacityByUnit[String(unit.id)]?.combined_enrolled_students ||
            capacityByUnit[String(unit.id)]?.enrolled_students ||
            0,
        ),
        timetable_combined_booked_students: Number(
          capacityByUnit[String(unit.id)]?.combined_booked_students ||
            capacityByUnit[String(unit.id)]?.booked_students ||
            0,
        ),
      }))
      .sort((left, right) =>
        String(left.code || "").localeCompare(String(right.code || "")),
      );
  },

  async getSettings(academicYearId, semesterId) {
    if (!academicYearId || !semesterId) return null;
    const { data, error } = await supabase
      .from("summative_assessment_timetable_settings")
      .select("*")
      .eq("academic_year_id", academicYearId)
      .eq("semester_id", semesterId)
      .maybeSingle();
    if (error)
      throw readableError(
        error,
        "Assessment timetable settings are unavailable.",
      );
    return data || null;
  },

  async getRooms() {
    const primary = await supabase
      .from("rooms")
      .select("id, code, name, room_type, capacity, status, location, department_id")
      .order("name", { ascending: true });
    if (!primary.error)
      return (primary.data || []).filter(
        (room) => String(room?.status || "active").toLowerCase() === "active",
      );

    // Some catalogues use status but omit code/location; use a minimal shape
    // before falling back to the older is_active variant.
    const statusFallback = await supabase
      .from("rooms")
      .select("id, name, room_type, capacity, status, department_id")
      .order("name", { ascending: true });
    if (!statusFallback.error)
      return (statusFallback.data || []).filter(
        (room) => String(room?.status || "active").toLowerCase() === "active",
      );

    const fallback = await supabase
      .from("rooms")
      .select("id, name, room_type, capacity, is_active, department_id")
      .eq("is_active", true)
      .order("name", { ascending: true });
    if (fallback.error)
      throw readableError(
        primary.error,
        "Active assessment venues could not be loaded.",
      );
    return (fallback.data || []).map((room) => ({
      ...room,
      status: room.is_active === false ? "inactive" : "active",
    }));
  },

  async getTeachingRoomAssignments(academicYearId, semesterId) {
    if (!academicYearId || !semesterId) return [];
    const { data, error } = await supabase.rpc(
      "get_summative_assessment_teaching_rooms",
      {
        target_year: academicYearId,
        target_semester: semesterId,
      },
    );
    if (!error) return data || [];

    // Keep existing installations usable while the new scoped RPC is being
    // applied. The regular teaching-timetable RPC returns the same room
    // assignments, but its visibility is narrower for department users.
    if (
      error?.code === "PGRST202" ||
      error?.code === "42883" ||
      /does not exist|schema cache|could not find the function/i.test(
        error?.message || "",
      )
    ) {
      const rows = await timetableService.getSessions(academicYearId, semesterId);
      return (rows || [])
        .map((row) => ({
          unit_id: row?.session?.unit_id || row?.unit?.id || row?.unit_id,
          room_id: row?.session?.room_id || row?.room?.id || row?.room_id,
        }))
        .filter((row) => row.unit_id && row.room_id);
    }
    throw readableError(
      error,
      "Teaching timetable rooms could not be loaded for this academic period.",
    );
  },

  async saveSettings({
    academicYearId,
    semesterId,
    assessmentStartsAt,
    assessmentEndsAt,
    breakStartsAt,
    breakEndsAt,
    periodStartsOn,
    periodEndsOn,
    practicalStartsOn,
  }) {
    const row = {
      academic_year_id: academicYearId,
      semester_id: semesterId,
      assessment_starts_at: assessmentStartsAt || null,
      assessment_ends_at: assessmentEndsAt || null,
      break_starts_at: breakStartsAt || null,
      break_ends_at: breakEndsAt || null,
      assessment_starts_on: periodStartsOn || null,
      assessment_ends_on: periodEndsOn || null,
      practical_assessment_starts_on:
        practicalStartsOn || periodStartsOn || null,
    };
    const { data, error } = await supabase
      .from("summative_assessment_timetable_settings")
      .upsert(row, { onConflict: "academic_year_id,semester_id" })
      .select()
      .single();
    if (error)
      throw readableError(
        error,
        "Assessment timetable settings could not be saved.",
      );
    return data;
  },

  async getEntries(academicYearId, semesterId) {
    if (!academicYearId || !semesterId) return [];
    const { data, error } = await supabase
      .from("summative_assessment_timetable_entries")
      .select(
        "*, unit:units(id, code, name, program_id, program_level, module_in_level, module, allow_combined_delivery, teaching_group_code, required_room_type, room_type, exam_theory_hours, exam_practical_hours, program:program_id(id, name, code, department_id))",
      )
      .eq("academic_year_id", academicYearId)
      .eq("semester_id", semesterId)
      .neq("status", "cancelled")
      .order("scheduled_date", { ascending: true })
      .order("starts_at", { ascending: true });
    if (error)
      throw readableError(
        error,
        "Assessment timetable entries are unavailable.",
      );
    return attachRooms(data || []);
  },

  async saveEntry({
    academicYearId,
    semesterId,
    unitId,
    assessmentComponent,
    scheduledDate,
    startsAt,
    endsAt,
    sessionId,
    roomId,
    notes,
  }) {
    const row = {
      academic_year_id: academicYearId,
      semester_id: semesterId,
      unit_id: unitId,
      assessment_component: assessmentComponent,
      scheduled_date: scheduledDate,
      starts_at: startsAt,
      ends_at: endsAt,
      session_id: sessionId || null,
      room_id: roomId || null,
      notes: notes || null,
      status: "draft",
    };
    const { data, error } = await supabase
      .from("summative_assessment_timetable_entries")
      .upsert(row, {
        onConflict: "academic_year_id,semester_id,unit_id,assessment_component",
      })
      .select(
        "*, unit:units(id, code, name, program_id, program_level, module_in_level, module, allow_combined_delivery, teaching_group_code, required_room_type, room_type, exam_theory_hours, exam_practical_hours, program:program_id(id, name, code, department_id))",
      )
      .single();
    if (error)
      throw readableError(
        error,
        "Assessment timetable entry could not be saved.",
      );
    const [withRoom] = await attachRooms([data]);
    return withRoom;
  },

  async cancelEntry(id) {
    const { data, error } = await supabase
      .from("summative_assessment_timetable_entries")
      .update({ status: "cancelled" })
      .eq("id", id)
      .select()
      .single();
    if (error)
      throw readableError(
        error,
        "Assessment timetable entry could not be removed.",
      );
    return data;
  },
};
